import { useQuery } from '@tanstack/react-query'
import { useParams, useLocation, Routes, Route, Navigate } from 'react-router-dom'
import QueryState from '../query_state'
import { Badge } from '@cinesend/ui-kit'
import useUtilities from '../../hooks/use_utilities'
import AssetsCollectionTable from '../assets_table/sub_assets_table'
import Page from '../layouts/page'
import AssetIdentifier from './asset_identifier'
import Details from './details'
import Metadata from './metadata'
import { statusTypes } from '../../constants/status_colour_map'

const Asset = ({ cycle, listPath, listLabel }) => {
  const { assetID, parentAssetID } = useParams()
  const { pathname } = useLocation()
  const { data: asset, isInitialLoading, isError, error } = useQuery([`cycles/${cycle.id}/assets/${assetID}`])
  const { assetTypes } = useUtilities()
  const childAssetTypes = assetTypes.filter((type) => type.parent_type === asset?.asset_type)
  const parentAssetType = assetTypes.find((type) => type.value === asset?.parent_asset_type)
  const pathEnd = pathname.split('/').filter(Boolean).pop()
  const onChildCollectionTab = childAssetTypes.some((type) => type.route_value === pathEnd)

  const breadcrumb =
    parentAssetType && parentAssetID
      ? [
          {
            text: parentAssetType?.plural_label,
            to: `/airlines/${asset?.cycle?.airline_id}/cycles/${asset?.cycle.id}/metadata/${parentAssetType?.route_value}`,
          },
          {
            text: asset?.parent_asset?.default_title ?? asset?.parent_asset?.title,
            to: `/airlines/${asset?.cycle?.airline_id}/cycles/${asset?.cycle.id}/metadata/${parentAssetType?.route_value}/${parentAssetID}`,
          },
          {
            text: asset?.plural_label,
            to: `/airlines/${asset?.cycle?.airline_id}/cycles/${asset?.cycle.id}/metadata/${parentAssetType?.route_value}/${parentAssetID}/${asset?.route_value}`,
          },
        ]
      : [
          {
            text: listLabel || asset?.plural_label,
            to:
              listPath ||
              `/airlines/${asset?.cycle?.airline_id}/cycles/${asset?.cycle.id}/metadata/${asset?.route_value}`,
          },
        ]

  const tabs = [
    { name: 'Details', to: 'details' },
    { name: 'Metadata', to: 'metadata' },
    ...childAssetTypes.map((assetType) => ({
      name: assetType.plural_label,
      to: `${assetType.route_value}`,
    })),
  ]

  return (
    <Page
      className={onChildCollectionTab ? undefined : 'cs-page-narrow'}
      breadcrumbs={breadcrumb.concat([
        {
          text: asset?.default_title ?? asset?.title,
        },
      ])}
      tabs={tabs}
      headerExtra={
        asset ? (
          <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-1'>
            <Badge tone={statusTypes[asset.status?.toLowerCase()] ?? 'neutral'}>{asset.status ?? 'N/A'}</Badge>
            <span aria-hidden='true'>·</span>
            <AssetIdentifier uuid={asset.uuid} />
          </span>
        ) : null
      }
    >
      <QueryState isLoading={isInitialLoading} error={isError ? error : null}>
        <Routes>
          {childAssetTypes?.map((assetType) => (
            <Route
              key={assetType.value}
              path={`/${assetType.route_value}`}
              element={
                <AssetsCollectionTable
                  parentAsset={asset}
                  cycle={cycle}
                  assetId={assetID}
                  assetType={assetType}
                  childAssets={asset?.child_assets}
                />
              }
            />
          ))}
          <Route
            path='/details'
            element={
              asset ? (
                <Details asset={asset} airlineID={cycle?.airline_id} cycleID={cycle.id} listPath={listPath} />
              ) : null
            }
          />
          <Route
            path='/metadata'
            element={asset ? <Metadata asset={asset} airlineID={cycle?.airline_id} cycleID={cycle.id} /> : null}
          />
          <Route path='*' element={<Navigate to='details' replace={true} />} />
        </Routes>
      </QueryState>
    </Page>
  )
}

export default Asset
